import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../../../lib/supabase';
import { getGardenName, getGardenSlug } from '../../../../../lib/garden';

// 園名・スラッグは lib/garden.ts が正本（ここに写さない）

interface Worker {
  id: string;
  name: string;
  email: string;
  qualifications: string[];
  created_at: string;
  averageScore: number;
  rank: string;
}

interface Evaluation {
  id: string;
  worker_id: string;
  顧客目線_rating: number;
  笑顔_rating: number;
  園の方針_rating: number;
  事前準備_rating: number;
  雰囲気_rating: number;
  運資質_rating: number;
  清潔感_rating: number;
  態度_rating: number;
  work_date: string;
  overall_comment: string;
  evaluator_name: string;
  created_at: string;
}

const GenderSilhouette = ({ isFemale, size = 48 }: { isFemale: boolean; size?: number }) => (
  <i
    className={isFemale ? 'ri-user-smile-line' : 'ri-user-line'}
    style={{ fontSize: size, color: '#b45309', lineHeight: 1 }}
  ></i>
);

const CATEGORIES = [
  { key: '顧客目線_rating', label: '顧客目線' },
  { key: '笑顔_rating', label: '笑顔' },
  { key: '園の方針_rating', label: '園の方針' },
  { key: '事前準備_rating', label: '事前準備' },
  { key: '雰囲気_rating', label: '雰囲気' },
  { key: '運資質_rating', label: '運資質' },
  { key: '清潔感_rating', label: '清潔感' },
  { key: '態度_rating', label: '態度' },
] as const;

// プレビュー用（Remixアイコン）
const DIRECT_SPOT_BENEFITS_S_ICONS = [
  { icon: 'ri-user-star-line', title: '正社員として直接雇用', desc: '待遇・条件は面談にてご相談。あなたに合ったキャリアを一緒に設計します。' },
  { icon: 'ri-trophy-line', title: '幹部候補としてのキャリアパス', desc: '園の未来を担うリーダーとして、成長の機会を全力でサポートします。' },
  { icon: 'ri-team-line', title: '園の運営・方針決定への参画', desc: 'スタッフの声が届く環境。あなたのアイデアが園づくりに活かされます。' },
  { icon: 'ri-heart-line', title: '充実した福利厚生', desc: '子育て支援・研修制度・有給取得推進など、長く働ける環境を整えています。' },
];

const DIRECT_SPOT_BENEFITS_A_ICONS = [
  { icon: 'ri-calendar-check-line', title: 'タイムーを介さず直接ご案内', desc: '弊園から直接シフトをご連絡。手数料なしで、より多くの報酬をお手元に。' },
  { icon: 'ri-time-line', title: 'あなたのペースで柔軟に', desc: 'ご都合に合わせたシフト調整が可能。無理なく続けられる働き方を実現。' },
  { icon: 'ri-customer-service-2-line', title: '専任担当者が毎回サポート', desc: '初回から安心。担当スタッフが丁寧にフォローします。' },
  { icon: 'ri-arrow-up-circle-line', title: '正社員登用の可能性', desc: '直接スポットから正社員へ。あなたの意欲次第でキャリアアップできます。' },
];

// HTMLメール用（絵文字）
const DIRECT_SPOT_BENEFITS_S_EMAIL = [
  { icon: '⭐', title: '正社員として直接雇用', desc: '待遇・条件は面談にてご相談。あなたに合ったキャリアを一緒に設計します。' },
  { icon: '🏆', title: '幹部候補としてのキャリアパス', desc: '園の未来を担うリーダーとして、成長の機会を全力でサポートします。' },
  { icon: '👥', title: '園の運営・方針決定への参画', desc: 'スタッフの声が届く環境。あなたのアイデアが園づくりに活かされます。' },
  { icon: '💛', title: '充実した福利厚生', desc: '子育て支援・研修制度・有給取得推進など、長く働ける環境を整えています。' },
];

const DIRECT_SPOT_BENEFITS_A_EMAIL = [
  { icon: '📅', title: 'タイムーを介さず直接ご案内', desc: '弊園から直接シフトをご連絡。手数料なしで、より多くの報酬をお手元に。' },
  { icon: '⏰', title: 'あなたのペースで柔軟に', desc: 'ご都合に合わせたシフト調整が可能。無理なく続けられる働き方を実現。' },
  { icon: '🎧', title: '専任担当者が毎回サポート', desc: '初回から安心。担当スタッフが丁寧にフォローします。' },
  { icon: '🔼', title: '正社員登用の可能性', desc: '直接スポットから正社員へ。あなたの意欲次第でキャリアアップできます。' },
];

const buildRadarPath = (values: number[], max: number, cx: number, cy: number, r: number): string => {
  const n = values.length;
  return values.map((v, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const ratio = v / max;
    const x = cx + r * ratio * Math.cos(angle);
    const y = cy + r * ratio * Math.sin(angle);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ') + ' Z';
};

const RadarChart = ({ evaluations }: { evaluations: Evaluation[] }) => {
  const cx = 200; const cy = 200; const r = 140;
  const n = CATEGORIES.length; const max = 10;
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const recentEvals = [...evaluations]
    .sort((a, b) => new Date(a.work_date).getTime() - new Date(b.work_date).getTime())
    .slice(-5);
  const colors = ['#c8a96e', '#a8895a', '#8a6e48', '#d4b882', '#e0cc9a'];
  const strokeColors = ['#b8944a', '#c8a96e', '#a8895a', '#d4b882', '#e0cc9a'];
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="420" viewBox="0 0 400 420" className="w-full max-w-sm mx-auto">
      <rect width="400" height="420" fill="transparent" />
      {gridLevels.map((level, li) => {
        const pts = CATEGORIES.map((_, i) => {
          const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
          const x = cx + r * level * Math.cos(angle);
          const y = cy + r * level * Math.sin(angle);
          return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
        }).join(' ') + ' Z';
        return <path key={li} d={pts} fill="none" stroke="#d4c4a8" strokeWidth="0.8" opacity="0.7" />;
      })}
      {CATEGORIES.map((_, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const x = cx + r * Math.cos(angle); const y = cy + r * Math.sin(angle);
        return <line key={i} x1={cx} y1={cy} x2={x.toFixed(2)} y2={y.toFixed(2)} stroke="#d4c4a8" strokeWidth="0.8" opacity="0.7" />;
      })}
      {recentEvals.map((ev, idx) => {
        const values = CATEGORIES.map(cat => (ev[cat.key as keyof Evaluation] as number) || 0);
        const path = buildRadarPath(values, max, cx, cy, r);
        return <path key={idx} d={path} fill={colors[idx % colors.length]} fillOpacity={0.35 + idx * 0.08} stroke={strokeColors[idx % strokeColors.length]} strokeWidth="1.5" />;
      })}
      {CATEGORIES.map((cat, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const labelR = r + 24;
        const x = cx + labelR * Math.cos(angle); const y = cy + labelR * Math.sin(angle);
        return <text key={i} x={x.toFixed(2)} y={y.toFixed(2)} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="#7a6a52" fontFamily="'Hiragino Kaku Gothic Pro', 'Meiryo', sans-serif">{cat.label}</text>;
      })}
      <g transform="translate(0,390)">
        {recentEvals.map((ev, idx) => {
          const x = 10 + idx * 76;
          return (
            <g key={idx}>
              <rect x={x} y={0} width={10} height={10} fill={strokeColors[idx % strokeColors.length]} rx="2" />
              <text x={x + 14} y={9} fontSize="9" fill="#9a8a72" fontFamily="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">{ev.work_date}</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

const LineBubble = ({ comment, evaluator, idx }: { comment: string; evaluator: string; idx: number }) => {
  const avatarColors = ['bg-amber-200', 'bg-stone-200', 'bg-orange-100'];
  const avatarColor = avatarColors[idx % avatarColors.length];
  const initial = evaluator.charAt(0);
  return (
    <div className="flex items-end gap-3 mb-4">
      <div className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center text-stone-700 text-sm font-bold shrink-0 border border-stone-200`}>
        {initial}
      </div>
      <div className="flex flex-col gap-1 max-w-xs">
        <span className="text-xs text-stone-400 ml-1">{evaluator}さん</span>
        <div className="relative bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
          <p className="text-sm text-stone-700 leading-relaxed">「{comment}」</p>
          <div className="absolute -left-2 bottom-3 w-0 h-0 border-t-8 border-t-transparent border-r-8 border-r-stone-200 border-b-0"></div>
          <div className="absolute -left-1.5 bottom-3 w-0 h-0 border-t-8 border-t-transparent border-r-8 border-r-white border-b-0"></div>
        </div>
      </div>
    </div>
  );
};

// SVGレーダーチャートをHTML文字列として生成
const buildRadarSvgString = (evaluations: Evaluation[]): string => {
  const cx = 200; const cy = 200; const r = 140;
  const n = CATEGORIES.length; const max = 10;
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const recentEvals = [...evaluations]
    .sort((a, b) => new Date(a.work_date).getTime() - new Date(b.work_date).getTime())
    .slice(-5);
  const colors = ['#c8a96e', '#a8895a', '#8a6e48', '#d4b882', '#e0cc9a'];
  const strokeColors = ['#b8944a', '#c8a96e', '#a8895a', '#d4b882', '#e0cc9a'];

  const gridPaths = gridLevels.map(level => {
    const pts = CATEGORIES.map((_, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const x = cx + r * level * Math.cos(angle);
      const y = cy + r * level * Math.sin(angle);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ') + ' Z';
    return `<path d="${pts}" fill="none" stroke="#d4c4a8" stroke-width="0.8" opacity="0.7"/>`;
  }).join('');

  const axisLines = CATEGORIES.map((_, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const x = cx + r * Math.cos(angle); const y = cy + r * Math.sin(angle);
    return `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(2)}" y2="${y.toFixed(2)}" stroke="#d4c4a8" stroke-width="0.8" opacity="0.7"/>`;
  }).join('');

  const dataPolygons = recentEvals.map((ev, idx) => {
    const values = CATEGORIES.map(cat => (ev[cat.key as keyof Evaluation] as number) || 0);
    const path = buildRadarPath(values, max, cx, cy, r);
    return `<path d="${path}" fill="${colors[idx % colors.length]}" fill-opacity="${(0.35 + idx * 0.08).toFixed(2)}" stroke="${strokeColors[idx % strokeColors.length]}" stroke-width="1.5"/>`;
  }).join('');

  const labels = CATEGORIES.map((cat, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const labelR = r + 24;
    const x = cx + labelR * Math.cos(angle); const y = cy + labelR * Math.sin(angle);
    return `<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" text-anchor="middle" dominant-baseline="middle" font-size="11" fill="#7a6a52" font-family="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">${cat.label}</text>`;
  }).join('');

  const legend = recentEvals.map((ev, idx) => {
    const x = 10 + idx * 76;
    return `<rect x="${x}" y="0" width="10" height="10" fill="${strokeColors[idx % strokeColors.length]}" rx="2"/><text x="${x + 14}" y="9" font-size="9" fill="#9a8a72" font-family="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">${ev.work_date}</text>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="420" viewBox="0 0 400 420" style="display:block;margin:0 auto;max-width:360px;">${gridPaths}${axisLines}${dataPolygons}${labels}<g transform="translate(0,390)">${legend}</g></svg>`;
};

// HTMLメール本文を生成
const generateHtmlEmail = (
  worker: Worker,
  evaluations: Evaluation[],
  mainBodyText: string[],
  positiveComments: { comment: string; evaluatorName: string }[],
  benefits: typeof DIRECT_SPOT_BENEFITS_S_EMAIL | null,
  subject: string
): string => {
  const radarSvg = evaluations.length > 0 && worker.rank !== 'ブロック' ? buildRadarSvgString(evaluations) : '';

  const scoreRows = [...evaluations]
    .sort((a, b) => new Date(b.work_date).getTime() - new Date(a.work_date).getTime())
    .slice(0, 5)
    .map(ev => {
      const total = CATEGORIES.reduce((s, c) => s + ((ev[c.key as keyof Evaluation] as number) || 0), 0);
      const cells = CATEGORIES.map(cat => `<td style="padding:6px 8px;text-align:center;color:#57534e;border-bottom:1px solid #f5f5f4;">${ev[cat.key as keyof Evaluation] as number}</td>`).join('');
      return `<tr><td style="padding:6px 10px;color:#78716c;border-bottom:1px solid #f5f5f4;">${ev.work_date}</td>${cells}<td style="padding:6px 10px;text-align:center;font-weight:700;color:#b45309;border-bottom:1px solid #f5f5f4;">${total}</td></tr>`;
    }).join('');

  const scoreTableHeaders = CATEGORIES.map(cat => `<th style="padding:8px;text-align:center;color:#78716c;font-weight:500;font-size:11px;">${cat.label}</th>`).join('');

  const benefitsHtml = benefits ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">${worker.rank === 'S' ? 'OFFER DETAILS' : 'DIRECT SPOT BENEFITS'}</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">
        <tr>
          ${benefits.slice(0, 2).map(b => `
          <td width="50%" style="padding:4px;">
            <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:14px;">
              <div style="font-size:20px;margin-bottom:6px;">${b.icon}</div>
              <div style="font-size:12px;font-weight:700;color:#1c1917;margin-bottom:4px;">${b.title}</div>
              <div style="font-size:11px;color:#78716c;line-height:1.6;">${b.desc}</div>
            </div>
          </td>`).join('')}
        </tr>
        <tr>
          ${benefits.slice(2, 4).map(b => `
          <td width="50%" style="padding:4px;">
            <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:14px;">
              <div style="font-size:20px;margin-bottom:6px;">${b.icon}</div>
              <div style="font-size:12px;font-weight:700;color:#1c1917;margin-bottom:4px;">${b.title}</div>
              <div style="font-size:11px;color:#78716c;line-height:1.6;">${b.desc}</div>
            </div>
          </td>`).join('')}
        </tr>
      </table>
    </div>` : '';

  const commentsHtml = positiveComments.length > 0 && worker.rank !== 'ブロック' ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">STAFF VOICES</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <p style="font-size:11px;color:#a8a29e;text-align:center;margin:0 0 14px;">一緒に働いたきらりのスタッフが、こんなコメントを寄せています</p>
      <div style="background:#fafaf9;border-radius:16px;padding:16px;border:1px solid #f5f5f4;">
        ${positiveComments.map(c => `
        <div style="display:flex;align-items:flex-end;gap:10px;margin-bottom:14px;">
          <div style="width:34px;height:34px;border-radius:50%;background:#fde68a;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#44403c;flex-shrink:0;border:1px solid #e7e5e4;">${c.evaluatorName.charAt(0)}</div>
          <div>
            <div style="font-size:10px;color:#a8a29e;margin-bottom:3px;">${c.evaluatorName}さん</div>
            <div style="background:#fff;border:1px solid #e7e5e4;border-radius:14px 14px 14px 2px;padding:10px 14px;font-size:12px;color:#44403c;line-height:1.6;box-shadow:0 1px 3px rgba(0,0,0,0.05);">「${c.comment}」</div>
          </div>
        </div>`).join('')}
      </div>
    </div>` : '';

  const chartHtml = radarSvg ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">EVALUATION RADAR CHART</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <div style="background:#fafaf9;border:1px solid #f5f5f4;border-radius:12px;padding:16px;">
        ${radarSvg}
      </div>
      <div style="margin-top:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="flex:1;height:1px;background:#e7e5e4;"></div>
          <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">SCORE HISTORY</span>
          <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        </div>
        <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:11px;border-radius:10px;overflow:hidden;border:1px solid #f5f5f4;">
          <thead>
            <tr style="background:#fafaf9;">
              <th style="padding:8px 10px;text-align:left;color:#78716c;font-weight:500;font-size:11px;">日付</th>
              ${scoreTableHeaders}
              <th style="padding:8px 10px;text-align:center;color:#b45309;font-weight:700;font-size:11px;">合計</th>
            </tr>
          </thead>
          <tbody>${scoreRows}</tbody>
        </table>
      </div>
    </div>` : '';

  const bodyParagraphs = mainBodyText.map(t => `<p style="font-size:14px;color:#44403c;line-height:1.8;margin:0 0 14px;">${t}</p>`).join('');

  return `<!DOCTYPE html>
<html lang="ja">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><title>${subject}</title></head>
<body style="margin:0;padding:0;background:#f5f5f4;font-family:'Hiragino Kaku Gothic Pro','Meiryo','Yu Gothic',sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f4;padding:32px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);">
        <tr>
          <td style="background:#fffbeb;padding:20px 32px;border-bottom:1px solid #fde68a;">
            <p style="margin:0;font-size:11px;color:#92400e;font-weight:600;letter-spacing:0.08em;">${gardenName}　採用担当</p>
            <p style="margin:6px 0 0;font-size:16px;font-weight:700;color:#1c1917;">${subject}</p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 32px 8px;">
            <p style="font-size:15px;font-weight:700;color:#1c1917;margin:0 0 20px;">${worker.name} 様</p>
            ${bodyParagraphs}
          </td>
        </tr>
        <tr>
          <td style="padding:0 32px;">
            ${benefitsHtml}
            ${commentsHtml}
            ${chartHtml}
          </td>
        </tr>
        <tr>
          <td style="padding:8px 32px 24px;">
            <p style="font-size:14px;color:#44403c;line-height:1.8;margin:0 0 14px;">ご興味がございましたら、お気軽にご返信ください。<br>${worker.name}様のご参加を、スタッフ一同楽しみにしています。</p>
          </td>
        </tr>
        <tr>
          <td style="background:#fafaf9;padding:16px 32px;border-top:1px solid #f5f5f4;">
            <p style="margin:0;font-size:12px;color:#a8a29e;">${gardenName}　採用担当</p>
            <p style="margin:2px 0 0;font-size:10px;color:#d6d3d1;">Kirari Nursery School — Recruitment</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
};

const WorkerOfferPage = () => {
  const { id } = useParams<{ id: string }>();
  const [worker, setWorker] = useState<Worker | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const gardenSlug = getGardenSlug();
  const gardenName = getGardenName(gardenSlug);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const { data: workerData, error: workerError } = await supabase
          .from('workers').select('*').eq('id', id).maybeSingle();
        if (workerError) throw workerError;
        if (!workerData) { setLoading(false); return; }

        const { data: evalsData, error: evalError } = await supabase
          .from('worker_evaluations').select('*').eq('worker_id', id).order('work_date', { ascending: false });
        if (evalError) throw evalError;

        let averageScore = 0;
        let rank = 'B';
        if (evalsData && evalsData.length > 0) {
          const totalScore = evalsData.reduce((sum, ev) => {
            return sum + CATEGORIES.reduce((s, cat) => s + ((ev[cat.key as keyof Evaluation] as number) || 5), 0);
          }, 0);
          averageScore = totalScore / evalsData.length;
          if (averageScore >= 60) rank = 'S';
          else if (averageScore >= 50) rank = 'A';
          else if (averageScore >= 40) rank = 'B';
          else rank = 'ブロック';
        }

        setWorker({
          id: workerData.id, name: workerData.name, email: workerData.email,
          qualifications: workerData.qualifications || [], created_at: workerData.created_at,
          averageScore, rank,
        });
        setEvaluations(evalsData || []);
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const getPositiveComments = () => {
    if (evaluations.length === 0) return [];
    return evaluations
      .filter(ev => ev.overall_comment && ev.overall_comment.trim().length > 0)
      .map(ev => ({ comment: ev.overall_comment.trim(), evaluatorName: ev.evaluator_name || '評価者' }))
      .slice(0, 2);
  };

  const getEmailSubject = () => {
    if (!worker) return '';
    switch (worker.rank) {
      case 'S': return `【特別オファー】${gardenName}より、${worker.name}様だけへのご招待`;
      case 'A': return `【直接スポットご招待】${worker.name}様の成長を、${gardenName}は見ています`;
      case 'B': return `【ご案内】${worker.name}様へ、${gardenName}からのお願い`;
      case 'ブロック': return '【ご連絡】今後のスポットワークについて';
      default: return '【案件のご案内】お仕事のお知らせ';
    }
  };

  const getMainBodyText = (): string[] => {
    if (!worker) return [];
    const name = worker.name;
    const score = worker.averageScore.toFixed(1);
    switch (worker.rank) {
      case 'S':
        return [
          `${name}様、突然のご連絡失礼いたします。${gardenName}　採用担当です。`,
          `タイムーを通じてご勤務いただいた期間、${name}様の評価スコアは平均 ${score}点。弊園でご勤務いただいた全スタッフの中でも、トップクラスの評価です。`,
          `きらり保育園では、タイムーからお越しいただくスポットワーカーさんを「大切な仲間」として迎えています。スタッフ全員で見守り、応援してきました。`,
          `そして今、${name}様にお伝えしたいことがあります。タイムーで来てくださる側から、きらり保育園でタイムーさんを迎える側へ。そんなマインドチェンジをしていただきたいのです。子どもたちのために、仲間として、ともに園をつくっていく存在になってほしい。そんな思いを込めて、正社員・幹部候補としてのオファーをお届けします。`,
        ];
      case 'A':
        return [
          `いつもタイムーを通じてきらり保育園にお越しいただき、ありがとうございます。${gardenName}　採用担当です。`,
          `弊園でのご勤務を重ねるたびに、${name}様の評価は着実に上がっています。現在の平均スコアは ${score}点。`,
          `きらり保育園では、スポットワーカーさんを「外部の方」とは思っていません。シフトに入ってくださる日は、スタッフ全員で温かくお迎えし、子どもたちのためにともに動く「仲間」として接しています。`,
          `ここで一つ、${name}様にご提案があります。タイムーで来てくださる側から、きらり保育園でタイムーさんを迎える側へ。そんなマインドチェンジのきっかけとして、「直接スポット」という形でご参加いただけませんか。`,
          `「まずは一度、きらり保育園直接スポットで試してみる」だけで構いません。`,
        ];
      case 'B':
        return [
          `いつもタイムーを通じてきらり保育園にお越しいただき、ありがとうございます。${gardenName}　採用担当です。`,
          `${name}様がご勤務いただいた日、子どもたちはとても楽しそうでした。きらり保育園では、タイムーからお越しいただくスポットワーカーさんのことを、スタッフ全員で温かく見守っています。`,
          `「今日来てくれた方、また来てほしいね」という声が、スタッフの間でよく上がっています。${name}様のことも、みんなで応援しています。`,
          `引き続き、タイムーにてきらり保育園のシフトをご確認いただき、ぜひまたお越しいただけますと大変嬉しく思います。`,
        ];
      default:
        return [
          'いつもきらり保育園のスポットワークにご参加いただき、誠にありがとうございます。',
          '誠に恐れ入りますが、現時点では継続的なご勤務のご案内が難しい状況となっております。',
        ];
    }
  };

  const getBenefitsEmail = () => {
    if (!worker) return null;
    if (worker.rank === 'S') return DIRECT_SPOT_BENEFITS_S_EMAIL;
    if (worker.rank === 'A') return DIRECT_SPOT_BENEFITS_A_EMAIL;
    return null;
  };

  const getBenefitsIcons = () => {
    if (!worker) return null;
    if (worker.rank === 'S') return DIRECT_SPOT_BENEFITS_S_ICONS;
    if (worker.rank === 'A') return DIRECT_SPOT_BENEFITS_A_ICONS;
    return null;
  };

  const getRankBadgeStyle = (rank: string) => {
    switch (rank) {
      case 'S': return 'bg-amber-100 text-amber-800 border border-amber-300';
      case 'A': return 'bg-stone-100 text-stone-700 border border-stone-300';
      case 'B': return 'bg-stone-50 text-stone-500 border border-stone-200';
      case 'ブロック': return 'bg-red-50 text-red-700 border border-red-200';
      default: return 'bg-stone-100 text-stone-600 border border-stone-200';
    }
  };

  const handleSend = async () => {
    if (!worker) return;
    setSending(true);
    const subject = getEmailSubject();
    const htmlContent = generateHtmlEmail(
      worker, evaluations, getMainBodyText(), getPositiveComments(), getBenefitsEmail(), subject
    );

    try {
      const { error } = await supabase.functions.invoke('send-offer-email', {
        body: { to: worker.email, subject, html: htmlContent, gardenName },
      });
      if (error) throw error;

      await supabase.from('offers_history').insert({
        worker_id: worker.id, rank: worker.rank, subject,
        body: htmlContent, sent_at: new Date().toISOString(),
      });

      setShowSuccess(true);
    } catch (e) {
      console.error('Error sending offer:', e);
      alert('送信に失敗しました。もう一度試してください。');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-amber-300 border-t-amber-700 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 text-sm">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-6">🔍</div>
          <h2 className="text-2xl font-bold text-stone-700 mb-4">ワーカーが見つかりません</h2>
          <Link to="/admin/workers" className="inline-flex items-center gap-2 px-6 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> ワーカー一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  const positiveComments = getPositiveComments();
  const mainBodyText = getMainBodyText();
  const benefitsIcons = getBenefitsIcons();
  const showChart = worker.rank !== 'ブロック' && evaluations.length > 0;
  const isFemale = parseInt(worker.id, 10) % 2 === 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to={`/admin/workers/${id}`} className="flex items-center gap-2 px-4 py-2 text-stone-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            詳細ページへ戻る
          </Link>
          <div className="h-6 w-px bg-stone-200"></div>
          <h1 className="text-lg font-semibold text-stone-800">オファーメール送信</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-amber-50 rounded-xl flex items-center justify-center border border-amber-200 overflow-hidden">
                <GenderSilhouette isFemale={isFemale} size={52} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-stone-800 mb-1">{worker.name}</h2>
                <p className="text-sm text-stone-500 mb-2">{worker.email}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {worker.qualifications.map((qual, idx) => (
                    <span key={idx} className="px-2.5 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200 whitespace-nowrap">{qual}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className={`px-5 py-2.5 rounded-xl text-xl font-bold whitespace-nowrap ${getRankBadgeStyle(worker.rank)}`}>
              {worker.rank}ランク
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-100 px-6 py-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-line text-amber-600 text-lg"></i>
              </div>
              <span className="text-xs text-stone-500 font-medium uppercase tracking-wider">メールプレビュー（送信内容と同一）</span>
            </div>
            <div className="ml-11">
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-stone-400 w-8 shrink-0">件名</span>
                <span className="text-sm font-semibold text-stone-800">{getEmailSubject()}</span>
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-xs text-stone-400 w-8 shrink-0">宛先</span>
                <span className="text-sm text-stone-600">{worker.name} 様　&lt;{worker.email}&gt;</span>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <p className="text-base font-semibold text-stone-800 mb-6">{worker.name} 様</p>
            <div className="space-y-4 mb-8">
              {mainBodyText.map((text, idx) => (
                <p key={idx} className="text-sm text-stone-700 leading-relaxed">{text}</p>
              ))}
            </div>

            {benefitsIcons && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-px flex-1 bg-stone-200"></div>
                  <span className="text-xs text-stone-400 uppercase tracking-wider px-2">
                    {worker.rank === 'S' ? 'Offer Details' : 'Direct Spot Benefits'}
                  </span>
                  <div className="h-px flex-1 bg-stone-200"></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {benefitsIcons.map((benefit, idx) => (
                    <div key={idx} className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3 items-start">
                      <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-amber-200 shrink-0">
                        <i className={`${benefit.icon} text-amber-600 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-800 mb-1">{benefit.title}</p>
                        <p className="text-xs text-stone-500 leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {positiveComments.length > 0 && worker.rank !== 'ブロック' && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-px flex-1 bg-stone-200"></div>
                  <span className="text-xs text-stone-400 uppercase tracking-wider px-2">Staff Voices</span>
                  <div className="h-px flex-1 bg-stone-200"></div>
                </div>
                <p className="text-xs text-stone-500 mb-4 text-center">一緒に働いたきらりのスタッフが、こんなコメントを寄せています</p>
                <div className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
                  {positiveComments.map((c, idx) => (
                    <LineBubble key={idx} comment={c.comment} evaluator={c.evaluatorName} idx={idx} />
                  ))}
                </div>
              </div>
            )}

            {showChart && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-px flex-1 bg-stone-200"></div>
                  <span className="text-xs text-stone-400 uppercase tracking-wider px-2">Evaluation Radar Chart</span>
                  <div className="h-px flex-1 bg-stone-200"></div>
                </div>
                <div className="bg-stone-50 border border-stone-100 rounded-xl p-4">
                  <RadarChart evaluations={evaluations} />
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-stone-200"></div>
                    <span className="text-xs text-stone-400 uppercase tracking-wider px-2">Score History</span>
                    <div className="h-px flex-1 bg-stone-200"></div>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-stone-100">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-stone-50 border-b border-stone-100">
                          <th className="px-3 py-2.5 text-left text-stone-500 font-medium">日付</th>
                          {CATEGORIES.map(cat => (
                            <th key={cat.key} className="px-2 py-2.5 text-center text-stone-500 font-medium">{cat.label}</th>
                          ))}
                          <th className="px-3 py-2.5 text-center text-amber-700 font-semibold">合計</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...evaluations]
                          .sort((a, b) => new Date(b.work_date).getTime() - new Date(a.work_date).getTime())
                          .slice(0, 5)
                          .map((ev, idx) => {
                            const total = CATEGORIES.reduce((s, c) => s + ((ev[c.key as keyof Evaluation] as number) || 0), 0);
                            return (
                              <tr key={idx} className="border-b border-stone-50 hover:bg-amber-50/30 transition-colors">
                                <td className="px-3 py-2.5 text-stone-500">{ev.work_date}</td>
                                {CATEGORIES.map(cat => (
                                  <td key={cat.key} className="px-2 py-2.5 text-center text-stone-600">{ev[cat.key as keyof Evaluation] as number}</td>
                                ))}
                                <td className="px-3 py-2.5 text-center font-bold text-amber-700">{total}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-stone-700 leading-relaxed mb-6">
              ご興味がございましたら、お気軽にご返信ください。<br />
              {worker.name}様のご参加を、スタッフ一同楽しみにしています。
            </p>
            <div className="border-t border-stone-100 pt-4">
              <p className="text-xs text-stone-400">{gardenName}　採用担当</p>
              <p className="text-xs text-stone-300 mt-0.5">Kirari Nursery School — Recruitment</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {[
            { key: 'worker', icon: 'ri-user-line', label: 'ワーカー詳細情報' },
            { key: 'target', icon: 'ri-send-plane-line', label: '送信対象' },
          ].map(({ key, icon, label }) => (
            <div key={key} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <button onClick={() => setExpandedSection(expandedSection === key ? null : key)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <i className={`${icon} text-amber-600 text-lg`}></i>
                  <span className="text-sm font-semibold text-stone-700">{label}</span>
                </div>
                <i className={`ri-arrow-${expandedSection === key ? 'up' : 'down'}-s-line text-xl text-stone-400`}></i>
              </button>
              {expandedSection === key && (
                <div className="px-5 py-4 border-t border-stone-100 bg-stone-50">
                  {key === 'worker' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div><div className="text-xs text-stone-400 mb-1">平均スコア</div><div className="text-lg font-bold text-amber-700">{worker.averageScore.toFixed(1)}点</div></div>
                      <div><div className="text-xs text-stone-400 mb-1">ランク</div><div className="text-lg font-bold text-stone-700">{worker.rank}ランク</div></div>
                      <div><div className="text-xs text-stone-400 mb-1">評価回数</div><div className="text-sm text-stone-700">{evaluations.length}回</div></div>
                      <div><div className="text-xs text-stone-400 mb-1">資格</div><div className="text-sm text-stone-700">{worker.qualifications.join(', ')}</div></div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-stone-100">
                      <i className="ri-mail-line text-amber-600 text-xl"></i>
                      <div>
                        <div className="text-sm font-semibold text-stone-700">{worker.name}</div>
                        <div className="text-xs text-stone-500">{worker.email}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <button
            onClick={handleSend}
            disabled={sending}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-base font-bold bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm whitespace-nowrap cursor-pointer"
          >
            {sending ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                送信中...
              </>
            ) : (
              <>
                <i className="ri-send-plane-2-line text-xl"></i>
                {worker.name}様にメールを送信する
              </>
            )}
          </button>
        </div>

        <div className="pb-8">
          <Link to={`/admin/workers/${id}`} className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-stone-200 text-stone-500 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors whitespace-nowrap w-full">
            <i className="ri-arrow-left-line"></i>
            詳細ページへ戻る
          </Link>
        </div>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-200">
              <i className="ri-check-line text-amber-700 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-stone-800 mb-2">送信しました</h3>
            <p className="text-sm text-stone-500 mb-5">{worker.name}様のメールアドレスにHTMLメールを送信しました。</p>
            <button onClick={() => setShowSuccess(false)} className="px-6 py-2 bg-amber-700 text-white text-sm rounded-lg hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap">
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerOfferPage;
